import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import AuthProvider from '@/context/AuthProvider';
import XeroProvider from '@/context/XeroProvider';
import HomeContextProvider from '@/context/HomeProvider';
import { routes } from '@/utils/routes';
import lazyWithRetry from '@/utils/lazyWithRetry';
import { KEEP_SEARCH_PARAMS_SESSION_STORAGE_NAME } from '@/utils/browserStorageNames';
import { isMobileView } from '@/utils/responsive';
import { isFeatureEnabled } from '@/config/featureFlags.service';
import { Feature } from '@/config/featureFlags.definition';
import ManagePaymentPage from '@/pages/Home/ManagePayment';

const LoginPage = lazyWithRetry(() => import('@/pages/Login'), 'login');
const HomePage = lazyWithRetry(() => import('@/pages/Home'), 'main_home');
const EmergeHomePage = lazyWithRetry(() => import('@/pages/Home/EmergeHome'), 'emerge_home');
const XeroIntegrationPage = lazyWithRetry(
  () => import('@/pages/Home/XeroIntegration'),
  'xero_home'
);
const NewBatchPaymentPage = lazyWithRetry(
  () => import('@/pages/Home/NewBatchPayment'),
  'new_batch_payment'
);
const GeneralLedgerCodeSettingsPage = lazyWithRetry(
  () => import('@/pages/Home/GeneralLedgerCodeSettings'),
  'general_ledger_settings'
);
const PasskeySettingsPage = lazyWithRetry(
  () => import('@/pages/Home/PasskeySettings'),
  'passkey_settings'
);
const TeamMembersPage = lazyWithRetry(() => import('@/pages/Home/TeamMembers'), 'team_members');
const CardsPage = lazyWithRetry(() => import('@/pages/Home/Cards'), 'cards');
const TransferPage = lazyWithRetry(
  () => import('@/pages/Home/TransferMoney'),
  'transfer_to_person_or_company'
);
const PaymentPage = lazyWithRetry(() => import('@/pages/Home/Payment'), 'pay_a_person_or_company');
const SelectedAccountPage = lazyWithRetry(
  () => import('@/pages/Home/SelectedAccount'),
  'selected_account'
);
const PageNotFound = lazyWithRetry(() => import('@/pages/common/PageNotFound'), 'page_not_found');

const MobileViewPage = lazyWithRetry(() => import('@/pages/common/MobilePage'), 'mobile_page');

/**
 * V2 App — Current production version
 * Top navigation, breadcrumbs, original UX/layout
 */
const V2App = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState<boolean>();

  const handleUnhandledRejection = (err: unknown) => {
    console.error(err);
  };

  useEffect(() => {
    const url = new URL(window.location.href);
    if (
      url.search &&
      (sessionStorage.getItem(KEEP_SEARCH_PARAMS_SESSION_STORAGE_NAME) == 'false' ||
        !sessionStorage.getItem(KEEP_SEARCH_PARAMS_SESSION_STORAGE_NAME))
    ) {
      sessionStorage.setItem(KEEP_SEARCH_PARAMS_SESSION_STORAGE_NAME, 'true');
      navigate({ pathname: routes.base, search: url.search });
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  useLayoutEffect(() => {
    setIsMobile(isMobileView());

    const handleResize = () => {
      setIsMobile(isMobileView());
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div id="App">
      <AuthProvider>
        <XeroProvider>
          <HomeContextProvider>
            {isMobile ? (
              <div className="min-w-dvw min-h-dvh max-w-dvw max-h-dvh h-full w-full font-sans">
                <MobileViewPage />
              </div>
            ) : (
              <div className="min-w-dvw min-h-dvh max-w-dvw max-h-dvh h-full w-full font-sans">
                <Routes>
                  <Route index element={<LoginPage />} />
                  <Route path={routes.home} element={<HomePage />}>
                    <Route index element={<EmergeHomePage />} />
                    <Route path={routes.transferMoney} element={<TransferPage />} />
                    {isFeatureEnabled(Feature.PAYMENT) && (
                      <Route path={routes.payment} element={<PaymentPage />} />
                    )}
                    {isFeatureEnabled(Feature.SELECTED_ACCOUNT) && (
                      <Route path={routes.account} element={<SelectedAccountPage />} />
                    )}
                    <Route path={routes.xero} element={<XeroIntegrationPage />} />
                    {isFeatureEnabled(Feature.BATCH_PAYMENT) && (
                      <Route path={routes.newBatchPayment} element={<NewBatchPaymentPage />} />
                    )}
                    {/* Feature.MANAGE_PAYMENT gates the dashboard itself (scheduled payments and
                        batch payment monitoring together) - Feature.BATCH_PAYMENT separately
                        gates only whether batch groups appear within it, inside
                        useManagePayment. */}
                    {isFeatureEnabled(Feature.MANAGE_PAYMENT) && (
                      <Route path={routes.managePayment} element={<ManagePaymentPage />} />
                    )}
                    <Route
                      path={routes.generalLedgerSettings}
                      element={<GeneralLedgerCodeSettingsPage />}
                    />
                    <Route path={routes.passkeySettings} element={<PasskeySettingsPage />} />
                    {isFeatureEnabled(Feature.TEAM_MEMBERS) && (
                      <Route path={routes.teamMembers} element={<TeamMembersPage />} />
                    )}
                    {isFeatureEnabled(Feature.CARDS) && (
                      <Route path={routes.cards} element={<CardsPage />} />
                    )}
                  </Route>
                  <Route path="*" element={<PageNotFound />} />
                </Routes>
              </div>
            )}
          </HomeContextProvider>
        </XeroProvider>
      </AuthProvider>
    </div>
  );
};

export default V2App;
